import { SanityImageSource } from '@sanity/image-url/lib/types/types'

import { SanityImage } from 'src/components/atoms/SanityImage'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { ImageTextRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { Text, TextValue } from 'src/components/molecules/sanity/Content'

import { CmsCtaButtonRebrand } from '../CmsCtaButtonRebrand'

export type ImageTextSectionRebrandProps = Omit<
  ImageTextRebrandResult,
  'description' | 'image' | 'direction'
> & {
  description?: TextValue | string
  image?: SanityImageSource
  imagePosition?: 'left' | 'right'
}

const BACKGROUND_CLASS: Record<
  NonNullable<ImageTextSectionRebrandProps['backgroundColor']>,
  string
> = {
  lavenderPale: 'bg-lavenderPale',
  limePale: 'bg-limePale',
  linen: 'bg-linen'
}

const ImageTextSectionRebrand = ({
  heading,
  image,
  ctas,
  description,
  imagePosition = 'left',
  eyebrow,
  backgroundColor = 'linen'
}: ImageTextSectionRebrandProps) => {
  const imagePositionClass =
    imagePosition === 'right' ? 'lg:flex-row-reverse' : 'lg:flex-row'

  return (
    <div className={`py-10 lg:py-22 ${BACKGROUND_CLASS[backgroundColor]}`}>
      <div
        className={`container mx-auto flex flex-col gap-4 lg:gap-28 lg:items-stretch px-4 lg:px-20 ${imagePositionClass}`}
      >
        {image && (
          <div className='relative w-full lg:flex-1 aspect-[5/4] overflow-hidden'>
            <SanityImage alt='' source={image} fill className='object-cover' />
          </div>
        )}
        <div className='flex flex-col gap-1 lg:flex-1 lg:self-stretch lg:justify-center'>
          {eyebrow && (
            <TypographyRebrand color='forestDeep' variant='eyebrow'>
              {eyebrow}
            </TypographyRebrand>
          )}
          {heading && (
            <TypographyRebrand color='forestDeep' variant='h2'>
              <Text
                components={{
                  block: ({ children }) => (
                    <span className='block'>{children}</span>
                  )
                }}
                value={heading}
              />
            </TypographyRebrand>
          )}
          {/* check when we have different description types */}
          {description && (
            <TypographyRebrand color='forestDeep' variant='introduction'>
              {typeof description === 'string' ? (
                description
              ) : (
                <Text
                  components={{
                    block: ({ children }) => (
                      <p className='mb-4 last:mb-0'>{children}</p>
                    )
                  }}
                  value={description}
                />
              )}
            </TypographyRebrand>
          )}
          {ctas && Array.isArray(ctas) ? (
            <div className='flex flex-row gap-2'>
              {ctas.map((ctaButton, idx) => (
                <CmsCtaButtonRebrand key={`cta_${idx}`} {...ctaButton} />
              ))}
            </div>
          ) : (
            ctas
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageTextSectionRebrand
